import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Users, Briefcase, Wrench, Crown, FileText, ScrollText, MessageSquare, Bell, Merge } from 'lucide-react';

/**
 * Customer Hub tabs — renders at the top of every customer-related admin page so staff can
 * jump between facets of the customer record without going back to the sidebar.
 *
 * Notes:
 *  - "Customer List" is the default landing tab (/admin/customers).
 *  - Customer Info, Jobs/Service History, and Communication don't have dedicated pages yet —
 *    they live in the per-customer drawer accessed by clicking a row. Those tabs are still
 *    rendered as informational nav cues but route back to the list with a small toast.
 *  - Merge Duplicates is permission-gated; we filter at the consumer level via userRole.
 */

const ADMIN_TIER = new Set(['super_admin', 'admin']);
const OFFICE_ALLOWED = new Set([...ADMIN_TIER, 'office_staff']);
const ALL_INTERNAL = new Set(['super_admin', 'admin', 'manager', 'service_manager', 'construction_manager', 'office_staff']);

const TABS = [
  { key: 'list',          label: 'Customer List',  path: '/admin/customers',            icon: Users,         allow: ALL_INTERNAL },
  { key: 'equipment',     label: 'Equipment',      path: '/admin/assets',               icon: Wrench,        allow: ALL_INTERNAL,
    hint: 'Customer-owned equipment & service history' },
  { key: 'memberships',   label: 'Memberships',    path: '/admin/memberships',          icon: Crown,         allow: ALL_INTERNAL },
  { key: 'invoices',      label: 'Invoices',       path: '/admin/invoices',             icon: FileText,      allow: ALL_INTERNAL },
  { key: 'estimates',     label: 'Estimates',      path: '/admin/estimates',            icon: ScrollText,    allow: ALL_INTERNAL },
  { key: 'reminders',     label: 'Reminders',      path: '/admin/reminders',            icon: Bell,          allow: ADMIN_TIER },
  { key: 'merge',         label: 'Merge Duplicates', path: '/admin/merge-customers',    icon: Merge,         allow: OFFICE_ALLOWED },
];

export const CustomerTabsBar = ({ userRole }) => {
  const location = useLocation();
  const visibleTabs = TABS.filter((t) => t.allow.has(userRole));
  if (visibleTabs.length === 0) return null;

  return (
    <div data-testid="customer-tabs-bar" className="bg-[#141414] border border-[#262626] rounded-md p-1 flex items-center gap-1 overflow-x-auto">
      {visibleTabs.map((t) => {
        const active = location.pathname === t.path
          || (t.key === 'list' && location.pathname.startsWith('/admin/customers') && !location.pathname.startsWith('/admin/customers/'));
        const Icon = t.icon;
        return (
          <Link
            key={t.key}
            to={t.path}
            data-testid={`customer-tab-${t.key}`}
            data-active={active}
            title={t.hint || t.label}
            className={`flex items-center gap-1.5 px-3 h-9 rounded text-xs font-medium whitespace-nowrap transition-colors ${
              active
                ? 'bg-[#D4AF37] text-[#0A0A0A]'
                : 'text-[#A3A3A3] hover:text-white hover:bg-[#1f1f1f]'
            }`}
          >
            <Icon className="w-3.5 h-3.5" /> {t.label}
          </Link>
        );
      })}
    </div>
  );
};

export default CustomerTabsBar;
